/**
 * <TutorOfferFlow> — le geste sur une offre de cours : l'ouvrir, l'accepter, ou retirer sa
 * candidature (template).
 *
 * Legacy `ProposalsTabScreen` → `handleAccept` / `handleWithdraw`, et le « Accepter » des pages d'un
 * lien. Deux `DialogM` enchaînés deviennent deux étapes d'une feuille (`<ParentFlowModal>`, un seul
 * en-tête, la barre de progression) :
 *  1. **L'offre** — son titre en question (« Nouvelle proposition de groupe », « … pour toi »,
 *     « Nouvelle séance d'essai », « Nouvelle proposition de remplacement »), ce qu'elle dit
 *     (`<TutorOfferDetails>`), et deux actions : « Accepter la proposition », « Masquer la
 *     proposition ». « Retour » était une sortie : c'est la croix.
 *  2. **La confirmation** — la question du legacy, selon l'offre (un essai, un nouvel élève ajouté à
 *     des séances existantes et/ou à une séance à créer, un groupe, un remplacement), l'offre
 *     redite, et pour un groupe l'engagement : « En candidatant, tu confirmes être prêt.e à assurer
 *     ces séances. Tu recevras une confirmation si c'est validé pour toi. » Une action — « Je
 *     candidate » ou « Je confirme que j'accepte » — et « Annuler ».
 *  Depuis la page d'un lien, la confirmation est seule (`entry="page"`) : l'offre est déjà à l'écran.
 *  - **Retirer sa candidature** — une offre candidatée : « Retirer ta candidature ? », une étape.
 *
 * Champion : la Zoé du premier `DialogM` n'est pas reprise — un champion n'apparaît que là où il agit,
 * jamais dans une boîte de dialogue (le livre V16).
 *
 * Contrôlé : l'étape, l'offre et l'envoi en cours arrivent en props.
 */
import React from "react"

import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { TutorOfferDetails } from "../../organisms/TutorOfferDetails"
import { ParentFlowModal } from "../ParentFlowModal"
import { type UITutorOffer, type UITutorOfferFlowStep } from "../../../ports/primary/ui/types/tutorDispoOffres"

const TEXTS = {
    name: "Répondre à une offre de cours",
    accept: "Accepter la proposition",
    hide: "Masquer la proposition",
    apply: "Je candidate",
    acceptSubstitution: "Je confirme que j'accepte",
    cancel: "Annuler",
    commitment: "En candidatant, tu confirmes être prêt.e à assurer ces séances.",
    confirmation: "Tu recevras une confirmation si c'est validé pour toi.",
    withdrawTitle: "Retirer ta candidature ?",
    withdrawBody: "Tu ne seras plus candidat.e pour cette proposition.",
    withdraw: "Retirer ma candidature",
}

/** Le titre de l'offre ouverte (`getTitle`). */
export function tutorOfferTitle(offer: UITutorOffer): string {
    if (offer.kind === "substitution") return "Nouvelle proposition de remplacement"
    if (offer.kind === "trial") return "Nouvelle séance d'essai"
    return offer.adhoc === true ? "Nouvelle proposition pour toi" : "Nouvelle proposition de groupe"
}

/**
 * La question de la confirmation (`confirmationText`), et sa suite quand elle en a une : « Es-tu
 * sûr.e de candidater ? Tu créeras un nouveau live et accueilleras l'élève dans tes séances
 * existantes. » se lit en titre et en première ligne, pas en un titre de trois lignes.
 */
function confirmQuestion(offer: UITutorOffer): { title: string; lead?: string } {
    if (offer.kind === "substitution") return { title: "Es-tu sûr.e d'accepter ce remplacement ?" }
    if (offer.kind === "trial") return { title: "Es-tu sûr.e de candidater à cette séance d'essai ?" }
    if (offer.adhoc === true) {
        const hasNew = offer.detail.lines.some((line) => line.kind === "new")
        const hasExisting = offer.detail.lines.some((line) => line.kind === "existing")
        if (hasNew && hasExisting) {
            return {
                title: "Es-tu sûr.e de candidater ?",
                lead: "Tu créeras un nouveau live et accueilleras l'élève dans tes séances existantes.",
            }
        }
        if (hasExisting) return { title: "Es-tu sûr.e d'accueillir cet élève dans tes séances existantes ?" }
    }
    return { title: "Es-tu sûr.e de candidater à cette proposition ?" }
}

export type TutorOfferFlowProps = {
    visible: boolean
    onClose: () => void
    offer: UITutorOffer
    step: UITutorOfferFlowStep
    /** `list` (défaut) : l'offre, puis la confirmation. `page` : la confirmation seule. */
    entry?: "list" | "page"
    // Les actions d'une étape sont facultatives : une surface qui n'ouvre pas cette étape ne les passe
    // pas (la page d'un lien n'ouvre que la confirmation), et une action absente n'est pas rendue.
    /** « Accepter la proposition » (l'offre) : passer à la confirmation. */
    onAccept?: () => void
    /** « Masquer la proposition » (l'offre). */
    onHide?: () => void
    /** « Je candidate » / « Je confirme que j'accepte ». */
    onConfirm: () => void
    /** « Retirer ma candidature » (le retrait). */
    onWithdraw?: () => void
    /** Retour de la confirmation à l'offre (`entry: "list"`). */
    onBack?: () => void
    /** La réponse qui part. */
    sending?: "accept" | "hide" | "withdraw" | null
    /** Un envoi refusé, dit dans l'étape. */
    errorMessage?: string
    testID?: string
}

export function TutorOfferFlow(props: TutorOfferFlowProps) {
    const {
        visible,
        onClose,
        offer,
        step,
        entry = "list",
        onAccept,
        onHide,
        onConfirm,
        onWithdraw,
        onBack,
        sending = null,
        errorMessage,
        testID,
    } = props
    const busy = sending != null
    const shell = { visible, onClose, title: TEXTS.name, errorMessage, testID }

    if (step === "withdraw") {
        return (
            <ParentFlowModal
                {...shell}
                stepTitle={TEXTS.withdrawTitle}
                step={1}
                totalSteps={1}
                footer={
                    <Stack gap="2">
                        {onWithdraw != null && (
                            <Button
                                label={TEXTS.withdraw}
                                color="danger"
                                onPress={onWithdraw}
                                loading={sending === "withdraw"}
                                disabled={busy}
                                fullWidth
                            />
                        )}
                        <Button label={TEXTS.cancel} variant="outline" onPress={onClose} fullWidth />
                    </Stack>
                }
            >
                <Text size="text-lg">{TEXTS.withdrawBody}</Text>
            </ParentFlowModal>
        )
    }

    if (step === "detail") {
        return (
            <ParentFlowModal
                {...shell}
                stepTitle={tutorOfferTitle(offer)}
                step={1}
                totalSteps={2}
                footer={
                    <Stack gap="2">
                        {onAccept != null && (
                            <Button label={TEXTS.accept} onPress={onAccept} disabled={busy} fullWidth />
                        )}
                        {onHide != null && (
                            <Button
                                label={TEXTS.hide}
                                variant="outline"
                                color="danger"
                                onPress={onHide}
                                loading={sending === "hide"}
                                disabled={busy}
                                fullWidth
                            />
                        )}
                    </Stack>
                }
            >
                <TutorOfferDetails detail={offer.detail} />
            </ParentFlowModal>
        )
    }

    const question = confirmQuestion(offer)
    const fromPage = entry === "page"
    const isGroup = offer.kind !== "substitution"

    return (
        <ParentFlowModal
            {...shell}
            stepTitle={question.title}
            step={fromPage ? 1 : 2}
            totalSteps={fromPage ? 1 : 2}
            onBack={fromPage ? undefined : onBack}
            footer={
                <Stack gap="2">
                    <Button
                        label={isGroup ? TEXTS.apply : TEXTS.acceptSubstitution}
                        onPress={onConfirm}
                        loading={sending === "accept"}
                        disabled={busy}
                        fullWidth
                    />
                    <Button label={TEXTS.cancel} variant="outline" onPress={onClose} fullWidth />
                </Stack>
            }
        >
            <Stack gap="4">
                {question.lead != null && <Text size="text-lg">{question.lead}</Text>}
                <TutorOfferDetails detail={offer.detail} />
                {isGroup && (
                    <Stack gap="1">
                        <Text size="text-md" weight="semibold">
                            {TEXTS.commitment}
                        </Text>
                        <Text size="text-md" color="text-muted">
                            {TEXTS.confirmation}
                        </Text>
                    </Stack>
                )}
            </Stack>
        </ParentFlowModal>
    )
}
